import React from "react";

import { businessHealthScorePalette } from "../../../../Utils/Helper/businessHealth";
import {
  extractFlattenedFieldAPI,
  isValidField as isValidFieldAPI,
  formatDateAPI,
  formatDateRangeAPI,
} from "../../../../Utils/WatchlistSummary/validation";

// 🎯 Types and Interfaces
interface NewsData {
  synopsis?: string;
  metadata?: {
    confidence_score?: number;
    last_updated?: string;
    data_freshness?: string;
    sources?: Array<string | { name: string }>;
  };
  newsMetrics?: {
    timespan?: string;
    mediaVelocity?: string;
    totalArticles?: number;
    sentimentDistribution?: Record<string, number>;
    sourceReliabilityScore?: number;
  };
  sentimentAnalysis?: {
    sentimentScore?: number;
    overallSentiment?: string;
    positiveThemes?: string[];
    negativeThemes?: string[];
    neutralCoverage?: string[];
  };
  trendAnalysis?: {
    emergingThemes?: string[];
    mediaAttention?: string;
    competitorMentions?: string[];
  };
  organizationContext?: {
    relevantToOrganization?: string;
    potentialRisks?: string[];
    potentialOpportunities?: string[];
  };
  relevantNews?: NewsArticle[];
}

interface NewsArticle {
  title?: string;
  summary?: string;
  content?: string;
  url?: string;
  link?: string;
  sentiment?: string;
  relevanceScore?: number;
  relevance?: number;
  source?: string;
  date?: string;
  publishedAt?: string;
  organizationImpact?: string;
}

interface ScoreBubbleProps {
  sectionScore: number;
  healthFlag: string;
}

interface NewsModalHeaderProps {
  accountName: string;
  sectionData: any;
  onToggleAudio?: () => void;
}

interface NewsModalBodyProps {
  synopsis?: string;
  confidenceScore?: number;
  dataFreshness?: string;
  lastUpdated?: string;
  sources?: Array<string | { name: string }>;
  sectionData: any;
}

// 🎯 Score Bubble Component
const NewsScoreBubble: React.FC<ScoreBubbleProps> = ({ sectionScore }) => {
  const getScoreColors = (score: number) => businessHealthScorePalette(score);

  const colors = getScoreColors(sectionScore);

  return (
    <div
      style={{
        position: "relative",
        width: "48px",
        height: "48px",
        flexShrink: 0,
      }}
    >
      {/* Outer circle */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          background: colors.outer,
          border: `2px solid ${colors.inner}`,
        }}
      />
      {/* Inner circle */}
      <div
        style={{
          position: "absolute",
          top: "6px",
          left: "6px",
          width: "36px",
          height: "36px",
          borderRadius: "50%",
          background: colors.inner,
          border: `1px solid ${colors.score}`,
        }}
      />
      {/* Score circle (superscript) */}
      <div
        style={{
          position: "absolute",
          top: "-2px",
          right: "-2px",
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          background: colors.score,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px solid white",
        }}
      >
        <span
          style={{
            color: "white",
            fontSize: "11px",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {Math.round(sectionScore)}
        </span>
      </div>
    </div>
  );
};

// 🎯 Modal Header Component
const NewsModalHeader: React.FC<NewsModalHeaderProps> = ({
  accountName,
  sectionData,
  onToggleAudio,
}) => {
  const sectionScore = sectionData?.sectionScore || 0;
  const healthFlag = sectionData?.sectionHealthFlag || "N/A";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "16px",
      }}
    >
      <NewsScoreBubble sectionScore={sectionScore} healthFlag={healthFlag} />
      <div>
        <h3 style={{ margin: 0, fontSize: "18px", color: "#8b5cf6" }}>
          <strong>{accountName}</strong> - <em>News Analysis</em>
        </h3>
      </div>
      <button
        id="news-audio-btn"
        className="audio-btn"
        title="Play news audio narration"
        onClick={onToggleAudio}
        style={{
          background: "none",
          border: "none",
          padding: "8px",
          borderRadius: "6px",
          cursor: "pointer",
          transition: "background-color 0.2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M11 5 6 9H2v6h4l5 4V5ZM15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      </button>
    </div>
  );
};

// 🎯 Badge Components
const HealthBadge: React.FC<{ flag: string; text: string }> = ({
  flag,
  text,
}) => {
  const bgColor =
    flag === "G" ? "#16a34a" : flag === "Y" ? "#d97706" : "#dc2626";

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        background: bgColor,
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 500,
      }}
    >
      <span style={{ color: "white" }}>Account Health:</span>
      <span style={{ color: "white", fontWeight: 700 }}>{text}</span>
    </div>
  );
};

const RelevanceBadge: React.FC<{ flag: string; text: string }> = ({ text }) => {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        background: "#8b5cf6",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 500,
      }}
    >
      <span style={{ color: "white" }}>Relevance to Smarsh:</span>
      <span style={{ color: "white", fontWeight: 700 }}>{text}</span>
    </div>
  );
};

// 🎯 Modal Body Component
const NewsModalBody: React.FC<NewsModalBodyProps> = ({
  synopsis,
  confidenceScore,
  dataFreshness,
  sources,
  sectionData,
}) => {
  const healthFlag = sectionData?.sectionHealthFlag || "N/A";
  const relevanceFlag = sectionData?.sectionRelevanceFlag || "N/A";

  const sourcesText = Array.isArray(sources)
    ? sources
        .map(s => (typeof s === "object" ? s?.name : s))
        .filter(Boolean)
        .join(", ")
    : null;

  const formatConfidenceScore = (score: any) => {
    if (score === null || score === undefined)
      return { display: "N/A", color: "#64748b" };
    if (typeof score === "number") {
      const percentValue =
        score <= 1 ? Math.round(score * 100) : Math.round(score);
      const display = percentValue + "%";

      let color = "#64748b";
      if (percentValue >= 80) {
        color = "#16a34a";
      } else if (percentValue >= 50) {
        color = "#d97706";
      } else {
        color = "#dc2626";
      }

      return { display, color };
    }
    return { display: "N/A", color: "#64748b" };
  };

  const confidenceData = formatConfidenceScore(confidenceScore);
  const freshnessDisplay = dataFreshness || "N/A";

  const getHealthText = (flag: string) => {
    switch (flag) {
      case "G":
        return "GREEN";
      case "Y":
        return "YELLOW";
      case "R":
        return "RED";
      default:
        return "N/A";
    }
  };

  const getRelevanceText = (flag: string) => {
    switch (flag) {
      case "H":
        return "HIGH";
      case "M":
        return "MEDIUM";
      case "L":
        return "LOW";
      default:
        return "N/A";
    }
  };

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      {/* Health and Relevance Badges */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          padding: "12px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <HealthBadge flag={healthFlag} text={getHealthText(healthFlag)} />
        <RelevanceBadge
          flag={relevanceFlag}
          text={getRelevanceText(relevanceFlag)}
        />
      </div>

      {synopsis ? (
        <div style={{ padding: "16px" }}>
          <p
            style={{
              margin: "0 0 12px 0",
              lineHeight: 1.6,
              fontSize: "14px",
              color: "#374151",
            }}
          >
            {synopsis}
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "12px",
              marginTop: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: "4px",
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                Confidence:
              </span>
              <span
                style={{
                  color: confidenceData.color,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {confidenceData.display}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "11px",
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: "4px",
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 500 }}>
                Freshness:
              </span>
              <span
                style={{
                  color: "#1e293b",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {freshnessDisplay}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: "11px",
                padding: "6px 8px",
                background: "#f8fafc",
                borderRadius: "4px",
              }}
            >
              <span
                style={{
                  color: "#64748b",
                  fontWeight: 500,
                  marginBottom: "2px",
                }}
              >
                Sources:
              </span>
              <span
                style={{
                  color: "#1e293b",
                  fontWeight: 600,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                }}
              >
                {sourcesText || "N/A"}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: "16px" }}>
          <p
            style={{
              margin: 0,
              color: "#64748b",
              fontStyle: "italic",
              fontSize: "14px",
            }}
          >
            No news metadata available for this account.
          </p>
        </div>
      )}
    </div>
  );
};

// 🎯 Organization Relevance Card Component
const OrganizationRelevanceCard: React.FC<{
  relevantToOrganization: any;
  organizationName?: string;
}> = ({ relevantToOrganization, organizationName }) => {
  if (!isValidFieldAPI(relevantToOrganization)) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          marginBottom: "16px",
          padding: "16px",
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>🎯</span> Relevance for {organizationName || "Organization"}
        </h4>
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          No organization relevance data available.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        padding: "16px",
      }}
    >
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: "16px",
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span>🎯</span> Relevance for {organizationName || "Organization"}
      </h4>
      <div
        style={{
          background: "#f3f4f6",
          borderLeft: "4px solid #8b5cf6",
          padding: "12px 16px",
          borderRadius: "8px",
        }}
      >
        <p
          style={{
            margin: 0,
            lineHeight: 1.6,
            fontSize: "14px",
            color: "#374151",
            fontWeight: 500,
          }}
        >
          {typeof relevantToOrganization === "string"
            ? relevantToOrganization
            : relevantToOrganization?.relevance ||
              "Relevance assessment not available"}
        </p>
      </div>
    </div>
  );
};

// 🎯 Key Metrics Card Component
const KeyMetricsCard: React.FC<{
  timespan?: any;
  mediaVelocity?: any;
  totalArticles?: any;
  sentimentDistribution?: any;
  sourceReliabilityScore?: any;
  overallSentiment?: any;
  sentimentScore?: any;
}> = ({
  timespan,
  mediaVelocity,
  totalArticles,
  sentimentDistribution,
  sourceReliabilityScore,
  overallSentiment,
  sentimentScore,
}) => {
  const hasData =
    isValidFieldAPI(timespan) ||
    isValidFieldAPI(mediaVelocity) ||
    isValidFieldAPI(totalArticles) ||
    isValidFieldAPI(sentimentDistribution) ||
    isValidFieldAPI(sourceReliabilityScore) ||
    isValidFieldAPI(overallSentiment) ||
    isValidFieldAPI(sentimentScore);

  const getMediaVelocityColor = (velocity: string) => {
    if (!velocity) return "#64748b";
    const lowerVelocity = velocity.toLowerCase();
    if (lowerVelocity === "high") return "#16a34a";
    if (lowerVelocity === "medium") return "#d97706";
    if (lowerVelocity === "low") return "#dc2626";
    return "#64748b";
  };

  const getSentimentColor = (sentiment: string) => {
    if (!sentiment) return "#64748b";
    const lowerSentiment = sentiment.toLowerCase();
    if (lowerSentiment === "positive") return "#16a34a";
    if (lowerSentiment === "negative") return "#dc2626";
    if (lowerSentiment === "neutral") return "#64748b";
    return "#64748b";
  };

  if (!hasData) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          marginBottom: "16px",
          padding: "16px",
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>📊</span> Key Metrics
        </h4>
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          No key metrics data available.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        padding: "16px",
      }}
    >
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: "16px",
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span>📊</span> Key Metrics
      </h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "12px",
        }}
      >
        {isValidFieldAPI(timespan) && (
          <div
            style={{
              background: "#f8fafc",
              padding: "10px",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                color: "#64748b",
                fontWeight: 500,
                marginBottom: "4px",
              }}
            >
              TIMESPAN
            </div>
            <div
              style={{ fontSize: "12px", color: "#1e293b", fontWeight: 600 }}
            >
              {formatDateRangeAPI(timespan)}
            </div>
          </div>
        )}
        {isValidFieldAPI(totalArticles) && (
          <div
            style={{
              background: "#f8fafc",
              padding: "10px",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                color: "#64748b",
                fontWeight: 500,
                marginBottom: "4px",
              }}
            >
              ARTICLES
            </div>
            <div
              style={{ fontSize: "12px", color: "#1e293b", fontWeight: 600 }}
            >
              {totalArticles}
            </div>
          </div>
        )}
        {isValidFieldAPI(mediaVelocity) && (
          <div
            style={{
              background: "#f8fafc",
              padding: "10px",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                color: "#64748b",
                fontWeight: 500,
                marginBottom: "4px",
              }}
            >
              VELOCITY
            </div>
            <div
              style={{
                fontSize: "12px",
                color: getMediaVelocityColor(mediaVelocity),
                fontWeight: 600,
                textTransform: "capitalize",
              }}
            >
              {mediaVelocity}
            </div>
          </div>
        )}
        {isValidFieldAPI(overallSentiment) && (
          <div
            style={{
              background: "#f8fafc",
              padding: "10px",
              borderRadius: "8px",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                color: "#64748b",
                fontWeight: 500,
                marginBottom: "4px",
              }}
            >
              SENTIMENT
            </div>
            <div
              style={{
                fontSize: "12px",
                color: getSentimentColor(overallSentiment),
                fontWeight: 600,
                textTransform: "capitalize",
              }}
            >
              {overallSentiment}
            </div>
          </div>
        )}
      </div>
      {sentimentDistribution &&
        Object.keys(sentimentDistribution).length > 0 && (
          <div style={{ marginTop: "16px" }}>
            <div
              style={{
                fontSize: "12px",
                color: "#64748b",
                fontWeight: 500,
                marginBottom: "8px",
              }}
            >
              SENTIMENT DISTRIBUTION
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {Object.entries(sentimentDistribution).map(([key, value]) => (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background:
                        key === "positive"
                          ? "#22c55e"
                          : key === "negative"
                            ? "#ef4444"
                            : "#64748b",
                    }}
                  />
                  <span
                    style={{
                      color: "#374151",
                      textTransform: "capitalize" as const,
                    }}
                  >
                    {`${key}: ${
                      typeof value === "number"
                        ? Math.round(value * 100) + "%"
                        : value
                    }`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

// 🎯 Sentiment Trend Analysis Card Component
const SentimentTrendAnalysisCard: React.FC<{
  positiveThemes?: string[];
  negativeThemes?: string[];
  emergingThemes?: string[];
  neutralCoverage?: string[];
  competitorMentions?: string[];
}> = ({
  positiveThemes,
  negativeThemes,
  emergingThemes,
  neutralCoverage,
  competitorMentions,
}) => {
  const hasValidPositive =
    Array.isArray(positiveThemes) &&
    positiveThemes.length > 0 &&
    positiveThemes.some(theme => isValidFieldAPI(theme));
  const hasValidNegative =
    Array.isArray(negativeThemes) &&
    negativeThemes.length > 0 &&
    negativeThemes.some(theme => isValidFieldAPI(theme));
  const hasValidEmerging =
    Array.isArray(emergingThemes) &&
    emergingThemes.length > 0 &&
    emergingThemes.some(theme => isValidFieldAPI(theme));
  const hasValidNeutral =
    Array.isArray(neutralCoverage) &&
    neutralCoverage.length > 0 &&
    neutralCoverage.some(theme => isValidFieldAPI(theme));
  const hasValidCompetitors =
    Array.isArray(competitorMentions) &&
    competitorMentions.length > 0 &&
    competitorMentions.some(mention => isValidFieldAPI(mention));

  const hasAnyData =
    hasValidPositive ||
    hasValidNegative ||
    hasValidEmerging ||
    hasValidNeutral ||
    hasValidCompetitors;

  if (!hasAnyData) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          marginBottom: "16px",
          padding: "16px",
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>📈</span> Sentiment Trend Analysis
        </h4>
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          No sentiment trend data available.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        padding: "16px",
      }}
    >
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: "16px",
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span>📈</span> Sentiment Trend Analysis
      </h4>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px",
        }}
      >
        {hasValidPositive && positiveThemes && (
          <div
            style={{
              background: "#dcfce7",
              border: "1px solid #bbf7d0",
              borderRadius: "8px",
              padding: "12px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#16a34a",
                fontWeight: 600,
                marginBottom: "8px",
                textTransform: "uppercase",
              }}
            >
              Positive Themes
            </div>
            <div
              style={{ fontSize: "13px", color: "#166534", lineHeight: 1.4 }}
            >
              {positiveThemes
                .filter(theme => isValidFieldAPI(theme))
                .slice(0, 3)
                .join(", ")}
            </div>
          </div>
        )}
        {hasValidNegative && (
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              padding: "12px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#dc2626",
                fontWeight: 600,
                marginBottom: "8px",
                textTransform: "uppercase",
              }}
            >
              Negative Themes
            </div>
            <div
              style={{ fontSize: "13px", color: "#991b1b", lineHeight: 1.4 }}
            >
              {negativeThemes
                .filter(theme => isValidFieldAPI(theme))
                .slice(0, 3)
                .join(", ")}
            </div>
          </div>
        )}
        {hasValidEmerging && (
          <div
            style={{
              background: "#dbeafe",
              border: "1px solid #bfdbfe",
              borderRadius: "8px",
              padding: "12px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#2563eb",
                fontWeight: 600,
                marginBottom: "8px",
                textTransform: "uppercase",
              }}
            >
              Emerging Themes
            </div>
            <div
              style={{ fontSize: "13px", color: "#1e40af", lineHeight: 1.4 }}
            >
              {emergingThemes
                .filter(theme => isValidFieldAPI(theme))
                .slice(0, 3)
                .join(", ")}
            </div>
          </div>
        )}
        {hasValidNeutral && (
          <div
            style={{
              background: "#f3f4f6",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              padding: "12px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#6b7280",
                fontWeight: 600,
                marginBottom: "8px",
                textTransform: "uppercase",
              }}
            >
              Neutral Coverage
            </div>
            <div
              style={{ fontSize: "13px", color: "#374151", lineHeight: 1.4 }}
            >
              {neutralCoverage
                .filter(theme => isValidFieldAPI(theme))
                .slice(0, 3)
                .join(", ")}
            </div>
          </div>
        )}
      </div>
      {hasValidCompetitors && (
        <div
          style={{
            marginTop: "16px",
            paddingTop: "12px",
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              fontWeight: 500,
              marginBottom: "8px",
            }}
          >
            <strong>Competitor Mentions</strong>
          </div>
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              padding: "8px",
            }}
          >
            <div
              style={{
                fontSize: "13px",
                color: "#dc2626",
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              {competitorMentions
                .filter(mention => isValidFieldAPI(mention))
                .slice(0, 5)
                .join(", ")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 🎯 Risks & Opportunities Card Component
const RisksOpportunitiesCard: React.FC<{
  potentialRisks?: string[];
  potentialOpportunities?: string[];
}> = ({ potentialRisks, potentialOpportunities }) => {
  const hasValidRisks =
    Array.isArray(potentialRisks) &&
    potentialRisks.length > 0 &&
    potentialRisks.some(risk => isValidFieldAPI(risk));
  const hasValidOpportunities =
    Array.isArray(potentialOpportunities) &&
    potentialOpportunities.length > 0 &&
    potentialOpportunities.some(opp => isValidFieldAPI(opp));

  if (!hasValidRisks && !hasValidOpportunities) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          marginBottom: "16px",
          padding: "16px",
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>⚖️</span> Risks & Opportunities
        </h4>
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          No risk or opportunity data available.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        padding: "16px",
      }}
    >
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: "16px",
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span>⚖️</span> Risks & Opportunities
      </h4>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}
      >
        <div>
          <div
            style={{
              fontSize: "14px",
              color: hasValidRisks ? "#dc2626" : "#6b7280",
              fontWeight: 600,
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>⚠️</span> Potential Risks
          </div>
          {hasValidRisks && potentialRisks ? (
            <div
              style={{
                background: "#fee2e2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "10px",
              }}
            >
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "16px",
                  fontSize: "13px",
                  color: "#dc2626",
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                {potentialRisks
                  .filter(risk => isValidFieldAPI(risk))
                  .slice(0, 5)
                  .map((risk, index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      {risk}
                    </li>
                  ))}
              </ul>
            </div>
          ) : (
            <div
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                fontStyle: "italic",
              }}
            >
              No risks identified
            </div>
          )}
        </div>
        <div>
          <div
            style={{
              fontSize: "14px",
              color: hasValidOpportunities ? "#16a34a" : "#6b7280",
              fontWeight: 600,
              marginBottom: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>📈</span> Opportunities
          </div>
          {hasValidOpportunities && potentialOpportunities ? (
            <div
              style={{
                background: "#dcfce7",
                border: "1px solid #bbf7d0",
                borderRadius: "8px",
                padding: "10px",
              }}
            >
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "16px",
                  fontSize: "13px",
                  color: "#16a34a",
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                {potentialOpportunities
                  .filter(opp => isValidFieldAPI(opp))
                  .slice(0, 5)
                  .map((opp, index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      {opp}
                    </li>
                  ))}
              </ul>
            </div>
          ) : (
            <div
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                fontStyle: "italic",
              }}
            >
              No opportunities identified
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 🎯 Relevant Stories Card Component
const RelevantStoriesCard: React.FC<{
  relevantNews?: NewsArticle[];
  organizationName?: string;
}> = ({ relevantNews, organizationName }) => {
  const hasValidArticles =
    Array.isArray(relevantNews) &&
    relevantNews.length > 0 &&
    relevantNews.some(
      article =>
        isValidFieldAPI(article?.title) || isValidFieldAPI(article?.summary)
    );

  const getSentimentBadgeColor = (sentiment: string) => {
    switch (sentiment.toLowerCase()) {
      case "positive":
        return "#22c55e";
      case "negative":
        return "#ef4444";
      default:
        return "#64748b";
    }
  };

  const getRelevanceBadgeColor = (score: number) => {
    if (score >= 0.8) return "#8b5cf6";
    if (score >= 0.5) return "#f59e0b";
    return "#6b7280";
  };

  const formatRelevanceLevel = (score: number) => {
    if (score >= 0.8) return "High";
    if (score >= 0.5) return "Medium";
    return "Low";
  };

  if (!hasValidArticles) {
    return (
      <div
        style={{
          background: "white",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          marginBottom: "16px",
          padding: "16px",
        }}
      >
        <h4
          style={{
            margin: "0 0 12px 0",
            fontSize: "16px",
            fontWeight: 600,
            color: "#374151",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>📄</span> Relevant Stories
        </h4>
        <p
          style={{
            margin: 0,
            color: "#64748b",
            fontStyle: "italic",
            fontSize: "14px",
          }}
        >
          No relevant news articles available.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        marginBottom: "16px",
        padding: "16px",
      }}
    >
      <h4
        style={{
          margin: "0 0 12px 0",
          fontSize: "16px",
          fontWeight: 600,
          color: "#374151",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span>📄</span> Relevant Stories
      </h4>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {relevantNews
          ?.filter(
            article =>
              isValidFieldAPI(article?.title) ||
              isValidFieldAPI(article?.summary)
          )
          .slice(0, 6)
          .map((article, index) => {
            const title = article?.title || "Untitled Article";
            const summary =
              article?.summary || article?.content || "No summary available";
            const url = article?.url || article?.link || "#";
            const sentiment = article?.sentiment || "neutral";
            const relevanceScore =
              article?.relevanceScore || article?.relevance || 0;
            const source = article?.source || "Unknown";
            const date = article?.date || article?.publishedAt || "";
            const organizationImpact = article?.organizationImpact || "";

            return (
              <div
                key={index}
                style={{
                  border: "1px solid #f3f4f6",
                  borderRadius: "8px",
                  padding: "12px",
                  transition: "border-color 0.2s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    marginBottom: "8px",
                  }}
                >
                  <h5
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#1e293b",
                      lineHeight: 1.4,
                      flex: 1,
                      marginRight: "12px",
                    }}
                  >
                    {url && url !== "#" ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#2563eb",
                          textDecoration: "underline",
                        }}
                      >
                        {title}{" "}
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ display: "inline", marginLeft: "4px" }}
                        >
                          <path d="M7 7h10v10" />
                          <path d="m7 17 10-10" />
                        </svg>
                      </a>
                    ) : (
                      title
                    )}
                  </h5>
                  <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                    <span
                      style={{
                        background: getSentimentBadgeColor(sentiment),
                        color: "white",
                        fontSize: "10px",
                        padding: "2px 6px",
                        borderRadius: "10px",
                        textTransform: "capitalize",
                      }}
                    >
                      {sentiment}
                    </span>
                    <span
                      style={{
                        background: getRelevanceBadgeColor(relevanceScore),
                        color: "white",
                        fontSize: "10px",
                        padding: "2px 6px",
                        borderRadius: "10px",
                      }}
                    >
                      {formatRelevanceLevel(relevanceScore)}
                    </span>
                  </div>
                </div>
                <p
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "13px",
                    color: "#64748b",
                    lineHeight: 1.4,
                  }}
                >
                  {summary.substring(0, 200)}
                  {summary.length > 200 ? "..." : ""}
                </p>
                {organizationImpact && (
                  <div
                    style={{
                      background: "#fef3c7",
                      borderLeft: "3px solid #f59e0b",
                      padding: "8px 12px",
                      margin: "8px 0",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#92400e",
                        fontWeight: 500,
                      }}
                    >
                      Significant for {organizationName || "Organization"}:
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#92400e",
                        marginTop: "2px",
                      }}
                    >
                      {organizationImpact}
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "11px",
                    color: "#9ca3af",
                    marginTop: "8px",
                  }}
                >
                  <span>{source}</span>
                  {date && <span>{formatDateAPI(date)}</span>}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

// 🎯 Main News Section Component
export const NewsSection: React.FC<{
  data: NewsData;
  sectionData: any;
  accountName: string;
  organizationName?: string;
  onToggleAudio?: () => void;
}> = ({ data, sectionData, accountName, organizationName, onToggleAudio }) => {
  const synopsis = extractFlattenedFieldAPI(data, "synopsis", null);
  const confidenceScore = extractFlattenedFieldAPI(
    data,
    "metadata.confidence_score",
    null
  );
  const lastUpdated = extractFlattenedFieldAPI(
    data,
    "metadata.last_updated",
    null
  );
  const dataFreshness = extractFlattenedFieldAPI(
    data,
    "metadata.data_freshness",
    null
  );
  const sources = extractFlattenedFieldAPI(data, "metadata.sources", []);

  const timespan = extractFlattenedFieldAPI(data, "newsMetrics.timespan", null);
  const mediaVelocity = extractFlattenedFieldAPI(
    data,
    "newsMetrics.mediaVelocity",
    null
  );
  const totalArticles = extractFlattenedFieldAPI(
    data,
    "newsMetrics.totalArticles",
    null
  );
  const sentimentDistribution = extractFlattenedFieldAPI(
    data,
    "newsMetrics.sentimentDistribution",
    {}
  );
  const sourceReliabilityScore = extractFlattenedFieldAPI(
    data,
    "newsMetrics.sourceReliabilityScore",
    null
  );

  const sentimentScore = extractFlattenedFieldAPI(
    data,
    "sentimentAnalysis.sentimentScore",
    null
  );
  const overallSentiment = extractFlattenedFieldAPI(
    data,
    "sentimentAnalysis.overallSentiment",
    null
  );
  const positiveThemes = extractFlattenedFieldAPI(
    data,
    "sentimentAnalysis.positiveThemes",
    []
  );
  const negativeThemes = extractFlattenedFieldAPI(
    data,
    "sentimentAnalysis.negativeThemes",
    []
  );
  const neutralCoverage = extractFlattenedFieldAPI(
    data,
    "sentimentAnalysis.neutralCoverage",
    []
  );

  const emergingThemes = extractFlattenedFieldAPI(
    data,
    "trendAnalysis.emergingThemes",
    []
  );
  const competitorMentions = extractFlattenedFieldAPI(
    data,
    "trendAnalysis.competitorMentions",
    []
  );

  const relevantToOrganization = extractFlattenedFieldAPI(
    data,
    "organizationContext.relevantToOrganization",
    null
  );
  const potentialRisks = extractFlattenedFieldAPI(
    data,
    "organizationContext.potentialRisks",
    []
  );
  const potentialOpportunities = extractFlattenedFieldAPI(
    data,
    "organizationContext.potentialOpportunities",
    []
  );

  const relevantNews = extractFlattenedFieldAPI(data, "relevantNews", []);

  return (
    <div className="news-section-consolidated">
      <NewsModalHeader
        accountName={accountName}
        sectionData={sectionData}
        onToggleAudio={onToggleAudio}
      />
      <NewsModalBody
        synopsis={synopsis}
        confidenceScore={confidenceScore}
        dataFreshness={dataFreshness}
        lastUpdated={lastUpdated}
        sources={sources}
        sectionData={sectionData}
      />
      <OrganizationRelevanceCard
        relevantToOrganization={relevantToOrganization}
        organizationName={organizationName}
      />
      <KeyMetricsCard
        timespan={timespan}
        mediaVelocity={mediaVelocity}
        totalArticles={totalArticles}
        sentimentDistribution={sentimentDistribution}
        sourceReliabilityScore={sourceReliabilityScore}
        overallSentiment={overallSentiment}
        sentimentScore={sentimentScore}
      />
      <SentimentTrendAnalysisCard
        positiveThemes={positiveThemes}
        negativeThemes={negativeThemes}
        emergingThemes={emergingThemes}
        neutralCoverage={neutralCoverage}
        competitorMentions={competitorMentions}
      />
      <RisksOpportunitiesCard
        potentialRisks={potentialRisks}
        potentialOpportunities={potentialOpportunities}
      />
      <RelevantStoriesCard
        relevantNews={relevantNews}
        organizationName={organizationName}
      />
    </div>
  );
};
